import {
  Box,
  Collapsible,
  Flex,
  HStack,
  IconButton,
  StackProps,
  Switch,
  Text,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { Form, Formik, FormikConfig } from "formik"
import SwitchField from "./SwitchField"

type MultiSwitchOption = {
  readonly label: string
  readonly field: string
  readonly value: boolean
  readonly icon?: IconName
}

type MultiSwitchFormikConfig<T extends MultiSwitchOption> = FormikConfig<{
  [K in T["field"]]: boolean
}>

// Explicitly omitting the second arg (formikHelpers) from submit handler
// since we can't provide it in call cases.
export type MultiSwitchDisclosureOnSubmit<T extends MultiSwitchOption> = (
  values: MultiSwitchFormikConfig<T>["initialValues"]
) => ReturnType<MultiSwitchFormikConfig<T>["onSubmit"]>

export type MultiSwitchDisclosureProps<
  T extends MultiSwitchOption = MultiSwitchOption
> = {
  singularOption: Omit<MultiSwitchOption, "value">
  // Requiring readonly means you must declare the multiOptions as const which
  // will ensure propper typing for the onSubmit values.
  multiOptions: readonly T[]
  onSubmit: MultiSwitchDisclosureOnSubmit<T>
} & Omit<StackProps, "onSubmit">

const MultiSwitchDisclosure = <T extends MultiSwitchOption>({
  singularOption,
  multiOptions,
  onSubmit,
  ...stackProps
}: MultiSwitchDisclosureProps<T>) => {
  // Every option on.
  const determinateOn = multiOptions.every(({ value }) => value)
  // Some options on.
  const indeterminate =
    !determinateOn && multiOptions.some(({ value }) => value)

  const { open, onToggle } = useDisclosure({
    // Initially show the multi switches when indeterminate.
    defaultOpen: indeterminate
  })

  // Get formik values from the expanded options.
  function getValues(getValue: (value: boolean) => boolean) {
    return multiOptions.reduce((acc, { field, value }) => {
      acc[field as keyof typeof acc] = getValue(value)
      return acc
    }, {} as MultiSwitchFormikConfig<T>["initialValues"])
  }

  const initialValues = getValues((value) => value)
  const reversedInitialValues = getValues((value) => !value)

  return (
    <>
      {/* TODO: use new CardLink (will require refactor to support children & probably will change component name) */}
      <HStack
        align="center"
        justify="space-between"
        w="100%"
        pointerEvents="auto"
        minW={200}
        color="gray.500"
        {...stackProps}
      >
        <Flex align="center" gap={2}>
          {!!singularOption.icon && <Pathicon icon={singularOption.icon} />}
          <Text fontSize="xs" my={0} fontWeight="normal">
            {singularOption.label}
          </Text>
        </Flex>
        {!indeterminate && (
          <Flex align="center" h={18}>
            {!open && (
              <Switch.Root
                colorPalette="brand"
                id={singularOption.field}
                checked={determinateOn}
                onChange={() => onSubmit(reversedInitialValues)}
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
            )}
            <IconButton
              aria-label="Toggle"
              variant="ghost"
              textDecor="none"
              cursor="pointer"
              ml={2}
              h={8}
              minW={8}
              borderRadius="full"
              onClick={onToggle}
            >
              <Pathicon
                color="gray.500"
                icon={open ? "chevron-down" : "chevron-right"}
              />
            </IconButton>
          </Flex>
        )}
      </HStack>
      <Box w="100%">
        <Collapsible.Root open={open}>
          <Collapsible.Content>
            <Formik
              initialValues={initialValues}
              onSubmit={onSubmit}
              enableReinitialize
            >
              <Form>
                <VStack align="start" gap={0} w="100%" bg="blackAlpha.100">
                  {multiOptions.map((option) => (
                    <SwitchField key={option.field} option={option} />
                  ))}
                </VStack>
              </Form>
            </Formik>
          </Collapsible.Content>
        </Collapsible.Root>
      </Box>
    </>
  )
}

export default MultiSwitchDisclosure
